import React from 'react';
import {
  BuildingStorefrontIcon,
  ChevronRightIcon,
  LinkSlashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import PlatformBadge from './PlatformBadge';
import PlatformLogo from '../ui/PlatformLogo';
import { getPlatform } from '../../constants/platforms';
import StorePerformanceMetrics from './StorePerformanceMetrics';
import { STORE_LIST_ROW_GRID } from './storeListGrid';
import { getStorePerformanceBadges } from '../../utils/storePerformanceBadges';
import { AttentionChip } from './StoreAttentionCenter';

function ConnectionBadge({ store, isDarkMode }) {
  if (store.isConnected) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-600 whitespace-nowrap">
        Connected
      </span>
    );
  }

  const isAdmin = store.connectionStatus === 'disconnected_admin';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
      isAdmin
        ? 'bg-red-500/15 text-red-600'
        : 'bg-amber-500/15 text-amber-700'
    }`}>
      {store.connectionStatusLabel || 'Disconnected'}
    </span>
  );
}

export default function StoreRow({
  store,
  storeGroup,
  onEnter,
  onDisconnect,
  onReconnect,
  isDarkMode,
  isAdmin = false,
  disconnecting = false,
  performanceSummaries = null,
  performanceLoading = false,
  layout = 'table',
  attention = null,
  showRegionHint = false,
}) {
  const variants = storeGroup?.stores?.length ? storeGroup.stores : [store];
  const activeStore = store || variants[0];

  const brand = activeStore.platformMeta?.brandColor || '#3b82f6';
  const isCachedView = !activeStore.isConnected;
  const displayName = storeGroup?.name || activeStore.name;
  const performanceSummary = performanceSummaries
    ? performanceSummaries[String(activeStore.profileId || activeStore.storeId)]
    : null;
  const perfBadges = performanceSummary
    ? getStorePerformanceBadges(performanceSummary)
    : [];

  const rowBg = !activeStore.isConnected
    ? (isDarkMode ? 'bg-amber-950/20' : 'bg-amber-50/40')
    : (isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50');

  if (layout === 'table') {
    return (
      <div
        className={`lg:grid ${STORE_LIST_ROW_GRID} px-4 py-3 border-b transition-colors ${rowBg} ${
          isDarkMode ? 'border-slate-800' : 'border-slate-100'
        }`}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white border border-slate-100"
        >
          {!activeStore.isConnected ? (
            <LinkSlashIcon className="h-5 w-5 text-amber-600" />
          ) : getPlatform(activeStore.platform).logo ? (
            <PlatformLogo
              platformId={activeStore.platform}
              size={activeStore.platform === 'amazon' ? 18 : 22}
              variant={activeStore.platform === 'amazon' ? 'wordmark' : 'mark'}
            />
          ) : (
            <BuildingStorefrontIcon className="h-5 w-5" style={{ color: brand }} />
          )}
        </div>

        <div className="min-w-0">
          <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {displayName}
          </div>
          <div className={`text-xs truncate ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            ID {activeStore.storeId}
            {showRegionHint && activeStore.region && activeStore.region !== '—'
              ? ` · ${activeStore.region}${activeStore.currency && activeStore.currency !== '—' ? ` · ${activeStore.currency}` : ''}`
              : ''}
          </div>
          {perfBadges.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {attention?.issues?.slice(0, 1).map((issue) => (
                <AttentionChip key={issue.key} issue={issue} isDarkMode={isDarkMode} compact />
              ))}
              {perfBadges.map((b) => (
                <span
                  key={b.key}
                  className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-medium ${b.className}`}
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="hidden lg:flex items-center min-w-0 overflow-visible">
          <PlatformBadge platformId={activeStore.platform} showLabel={false} />
        </div>

        <StorePerformanceMetrics
          layout="columns"
          summary={performanceSummary}
          currency={activeStore.currency}
          loading={performanceLoading}
          isDarkMode={isDarkMode}
        />

        <div className="flex items-center justify-end gap-1.5 flex-wrap">
          <ConnectionBadge store={activeStore} isDarkMode={isDarkMode} />
          {onEnter && (
            <button
              type="button"
              onClick={() => onEnter(activeStore)}
              className={`inline-flex items-center gap-0.5 px-2.5 py-1.5 rounded-md text-xs font-medium ${
                isCachedView
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
              }`}
            >
              {isCachedView ? 'View' : 'Open'}
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </button>
          )}
          {isCachedView && onReconnect && (
            <button
              type="button"
              onClick={() => onReconnect(store)}
              className="p-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700"
              title="Reconnect"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
            </button>
          )}
          {onDisconnect && activeStore.isConnected && (
            <button
              type="button"
              onClick={() => onDisconnect(activeStore)}
              disabled={disconnecting}
              className={`p-1.5 rounded-md border ${
                isDarkMode
                  ? 'border-red-800 text-red-400 hover:bg-red-950/50'
                  : 'border-red-200 text-red-600 hover:bg-red-50'
              } disabled:opacity-50`}
              title="Disconnect"
            >
              <LinkSlashIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  /* Card layout (mobile / legacy) */
  return (
    <div
      className={`w-full flex flex-col gap-3 px-4 py-4 rounded-xl border lg:hidden ${
        isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-slate-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${brand}18` }}
        >
          <BuildingStorefrontIcon className="h-5 w-5" style={{ color: brand }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{displayName}</div>
          <div className="text-xs text-slate-500">
            ID {activeStore.storeId}
            {showRegionHint && activeStore.region && activeStore.region !== '—'
              ? ` · ${activeStore.region}`
              : ''}
          </div>
        </div>
        <ConnectionBadge store={activeStore} isDarkMode={isDarkMode} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StorePerformanceMetrics
          layout="columns"
          summary={performanceSummary}
          currency={activeStore.currency}
          loading={performanceLoading}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
}
